import { Component, effect, inject, input, output, signal, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { KeyValueTypeModel } from '@models/common';
import { OperatorType } from '@models/helpers/operatorType';
import { OperatorTypeDisplayIdEnum, OperatorTypeIdEnum } from '@models/helpers/operatorTypeIdEnum';
import { FieldConditionModel } from '@models/module/workflowConditionalTransition';
import { CipoCheckboxComponent } from '@shared/components/fields/cipo-checkbox/cipo-checkbox.component';
import { CipoNumberComponent } from '@shared/components/fields/cipo-number/cipo-number.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoTextComponent } from '@shared/components/fields/cipo-text/cipo-text.component';
import {
  CipoCheckboxControl,
  CipoListControl,
  CipoNumberControl,
  CipoTextControl,
} from '@shared/components/fields/common';
import { ConfirmationDialog } from '@shared/dialogs/confirmation-dialog.component';
import { CipoFormModule } from '@shared/modules';
import { CipoDialogModule } from '@shared/modules/cipo-dialog.module';
import { CipoValidators } from '@shared/services/validators.service';
import { ConditionControlGroup } from '../condition-control-group';

@Component({
  selector: 'cipo-field-condition',
  templateUrl: './field-condition.component.html',
  styleUrls: ['./field-condition.component.scss'],
  standalone: true,
  imports: [
    CipoDialogModule,
    CipoSelectComponent,
    CipoTextComponent,
    CipoNumberComponent,
    CipoCheckboxComponent,
    CipoFormModule,
  ],
})
export class FieldConditionComponent {
  dialog = inject(MatDialog);

  fieldCondition = input<FieldConditionModel[]>([]);
  operators = input<KeyValueTypeModel<number>[]>([]);
  fieldDict = input<KeyValueTypeModel<number>[]>([]);

  conditionsChanged = output<FieldConditionModel[]>();
  fieldConditionsChanged = output<void>();
  fieldConditionsDirty = output<boolean>();
  fieldConditionsAllChanged = output<FieldConditionModel[]>();

  controls = signal<ConditionControlGroup[]>([]);
  operatorMap = signal<Record<number, KeyValueTypeModel<number>>>({});
  allConditions = signal<FieldConditionModel[]>([]);

  OperatorTypeEnum = OperatorType;
  OperatorTypeIdEnum = OperatorTypeIdEnum;
  OperatorTypeDisplayIdEnum = OperatorTypeDisplayIdEnum;

  constructor() {
    effect(() => {
      const input = this.fieldCondition();
      this.allConditions.set([...input]);
      this.controls.set(untracked(() => input.map(cond => this.createConditionControl(cond))));
    });
    effect(() => {
      this.operatorMap.set(
        this.operators().reduce(
          (map, op) => {
            map[op.key] = op;
            return map;
          },
          {} as Record<number, KeyValueTypeModel<number>>,
        ),
      );
    });
  }

  deleteFieldCondition(index: number) {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          title: 'complexConditions.general.deleteTitleCondition',
          description: 'complexConditions.general.deleteMessageFieldCondition',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (!!result) {
          const updated = this.allConditions().filter((_, i) => i !== index);
          this.allConditions.set(updated);
          this.controls.set(updated.map(cond => this.createConditionControl(cond)));
          this.fieldConditionsDirty.emit(true);
          this.fieldConditionsAllChanged.emit(updated);
        }
      });
  }

  updateConditions(newConditions: FieldConditionModel[]) {
    this.controls.set(newConditions.map(cond => this.createConditionControl(cond)));
    this.emitChanges(newConditions);
  }

  createConditionControl(cond: FieldConditionModel): ConditionControlGroup {
    const selectedField = this.fieldDict().find(f => f.key === cond.fieldId);
    const filteredOperators = this.operators().filter(
      op => op.typeId === selectedField?.typeId && op.displayTypeId === selectedField?.displayTypeId,
    );
    const selectOptions = [
      { key: 'val1', value: 'Value 1' },
      { key: 'val2', value: 'Value 2' },
    ];

    const fieldControl = new CipoListControl(cond.fieldId ?? '', {
      label: 'complexConditions.general.field',
      options: this.fieldDict().map(f => ({ key: f.key, value: f.value })),
      multiple: false,
      required: true,
    });

    const operatorControl = new CipoListControl(cond.operatorId ?? '', {
      label: 'complexConditions.general.operator',
      options: filteredOperators.map(o => ({ key: o.key, value: o.value })),
      multiple: false,
      required: true,
    });

    const controlGroup = {
      fieldControl,
      operatorControl,
      controlText: new CipoTextControl(cond.value?.toString() ?? '', { label: 'Value', required: true }),
      controlNumber: new CipoNumberControl(Number(cond.value) ?? 0, { label: 'Value', required: true }),
      secondControlNumber: new CipoNumberControl(typeof cond.value2 === 'number' ? cond.value2 : null, {
        label: 'complexConditions.general.secondValue',
        required: true,
      }),
      controlCheckbox: new CipoCheckboxControl(Boolean(cond.value)),
      valueControl: new CipoListControl(
        Array.isArray(cond.value) ? cond.value : cond.value != null && cond.value !== '' ? [cond.value] : [],
        {
          label: 'complexConditions.general.value',
          options: selectOptions,
          multiple: true,
          required: true,
        },
      ),
      selectedOperatorId: cond.operatorId ?? null,
    };

    controlGroup.secondControlNumber.setValidators([
      CipoValidators.secondGreaterThanFirstValidator(() => Number(controlGroup.controlNumber.value)),
    ]);
    controlGroup.secondControlNumber.updateValueAndValidity();

    fieldControl.valueChanges.subscribe(newFieldId => {
      operatorControl.setValue('');
      controlGroup.selectedOperatorId = null;
      controlGroup.controlText.setValue('');
      controlGroup.controlNumber.setValue(null);
      controlGroup.secondControlNumber.setValue(null);
      controlGroup.controlCheckbox.setValue(false);
      controlGroup.valueControl.setValue('');
      this.onFieldOrOperatorChanged();
      this.fieldConditionsDirty.emit(true);
    });
    operatorControl.valueChanges.subscribe(newOperatorId => {
      controlGroup.selectedOperatorId = newOperatorId;
      this.onFieldOrOperatorChanged();
      this.fieldConditionsDirty.emit(true);
    });
    controlGroup.controlText.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.fieldConditionsDirty.emit(true);
    });
    controlGroup.controlNumber.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.fieldConditionsDirty.emit(true);
    });
    controlGroup.secondControlNumber.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.fieldConditionsDirty.emit(true);
    });
    controlGroup.controlCheckbox.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.fieldConditionsDirty.emit(true);
    });
    controlGroup.valueControl.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.fieldConditionsDirty.emit(true);
    });

    return controlGroup;
  }

  onUserAction() {
    this.fieldConditionsChanged.emit();
    this.fieldConditionsDirty.emit(true);
  }

  onFieldOrOperatorChanged() {
    const models = this.getCurrentConditions();
    this.controls.set(models.map(cond => this.createConditionControl(cond)));
    this.emitChanges(models);
  }

  emitCurrentState() {
    const models = this.getCurrentConditions();
    this.allConditions.set(models);
    this.emitChanges(models);
  }

  getCurrentConditions(): FieldConditionModel[] {
    return this.controls().map(group => {
      const fieldId = Number(group.fieldControl.value);
      const operatorId = Number(group.selectedOperatorId ?? group.operatorControl?.value);
      const fieldMeta = this.fieldDict().find(f => f.key === fieldId);

      const value = this.getValueForOperator(group, operatorId);
      const value2 = operatorId === OperatorType.Between ? Number(group.secondControlNumber.value) : undefined;

      return {
        fieldId,
        operatorId,
        value,
        value2,
        fieldLabel: fieldMeta?.value ?? '',
        fieldTypeId: fieldMeta?.typeId ?? 0,
      };
    });
  }

  private emitChanges(models: FieldConditionModel[]) {
    this.fieldConditionsAllChanged.emit(models);
    const valid = models.filter(cond => this.isConditionValid(cond));
    this.conditionsChanged.emit(valid);
  }

  private getValueForOperator(group: ConditionControlGroup, opId: number): any {
    switch (opId) {
      case OperatorType.Contains:
      case OperatorType.StartsWith:
      case OperatorType.EndsWith:
      case OperatorType.Equals: {
        const val = group.controlText.value;
        const text = typeof val === 'string' ? val.trim() : Array.isArray(val) ? val.join(',') : '';
        return text ? text : null;
      }
      case OperatorType.DoesNotEqual:
      case OperatorType.LessThen:
      case OperatorType.GraterThen:
      case OperatorType.LessThenOrEqual:
      case OperatorType.GreaterThenOrEqual:
      case OperatorType.Between: {
        const num = Number(group.controlNumber.value);
        return Number.isNaN(num) ? null : num;
      }
      case OperatorType.True:
      case OperatorType.False:
        return !!group.controlCheckbox.value;
      default:
        return group.valueControl.value ?? null;
    }
  }

  private isConditionValid(cond: FieldConditionModel): boolean {
    if (!cond.fieldId || !cond.operatorId) return false;

    if (cond.operatorId === OperatorType.IsEmpty || cond.operatorId === OperatorType.IsNotEmpty) {
      return true;
    }
    if (cond.operatorId === OperatorType.Between) {
      const v1 = Number(cond.value);
      const v2 = Number((cond as any).value2 ?? null);
      return v1 !== null && v2 !== null && !Number.isNaN(v1) && !Number.isNaN(v2) && v2 > v1;
    }
    const op = this.operators().find(op => op.key === cond.operatorId);
    if (op && op.typeId === 1 && op.displayTypeId === 0) {
      return typeof cond.value === 'string' && cond.value.trim() !== '';
    }
    if (op && op.typeId === 3 && op.displayTypeId === 0) {
      return typeof cond.value === 'boolean';
    }
    if (op && op.typeId === 1 && op.displayTypeId === 3) {
      return Array.isArray(cond.value) && cond.value.length > 0;
    }
    if (op && op.typeId === 2 && op.displayTypeId === 0) {
      return cond.value !== null && cond.value !== '' && !Number.isNaN(Number(cond.value));
    }
    return false;
  }
}
